'use client';

import { isVoucherSalesRule, mapAllocationToSalesRule, useVoucherList } from './use-voucher-list';
import { useCouponList, removeUnavailableItems } from './use-coupon-list';
import type { CouponSectionType } from '../components/coupon-list/coupon-list.types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import type { UnifiedSalesRule } from '../types/sales-rule.types';
import { useHybridCartPrices } from '@kidslabs/module-quote';
import { checkoutStoreAPI } from '@kidslabs/module-checkout';

const sortByToDateAscending = (a: UnifiedSalesRule, b: UnifiedSalesRule) => {
    const dateA = a?.to_date ? new Date(a.to_date).getTime() : 0;
    const dateB = b?.to_date ? new Date(b.to_date).getTime() : 0;
    return dateA - dateB;
};

export const useSalesRuleList = () => {
    const { t } = useTranslation('flash-sale');
    const {
        availableItems: availableVouchers,
        unavailableItems: unavailableVouchers,
        appliedVouchers,
        loading: loadingVouchers,
        onRefresh: refreshVouchers,
    } = useVoucherList();
    const {
        validCoupons: availableCoupons,
        invalidCoupons: unavailableCoupons,
        appliedCoupons,
        onSelectCoupon,
        availableCoupon,
        loadingAvailableCoupons,
        loadingAppliedCoupons,
        loading: validatingCoupons,
        selectedCoupon,
        onRefresh: refreshCoupons,
    } = useCouponList();
    const { retryServerPrices } = useHybridCartPrices();

    const isAppliedSalesRule = useCallback(
        (rule: UnifiedSalesRule) => {
            if (isVoucherSalesRule(rule)) {
                return rule.name === appliedVouchers?.[0]?.allocation_name;
            }
            return rule.code === appliedCoupons?.[0]?.code;
        },
        [appliedCoupons, appliedVouchers]
    );

    const availableSalesRules = useMemo(() => {
        const vouchers = availableVouchers?.map(item =>
            mapAllocationToSalesRule(item.allocation, item.conditionResult)
        );
        const salesRules = [...vouchers, ...availableCoupons];
        const sorted = removeUnavailableItems(salesRules).sort(sortByToDateAscending);
        const appliedIndex = sorted.findIndex(isAppliedSalesRule);
        if (appliedIndex > 0) {
            const [applied] = sorted.splice(appliedIndex, 1);
            sorted.unshift(applied);
        }
        return sorted;
    }, [availableVouchers, availableCoupons, isAppliedSalesRule]);

    const unavailableSalesRules = useMemo(() => {
        const vouchers = unavailableVouchers?.map(item =>
            mapAllocationToSalesRule(item.allocation, item.conditionResult, item.ineligibilityReason)
        );
        return removeUnavailableItems([...vouchers, ...unavailableCoupons]).sort(sortByToDateAscending);
    }, [unavailableVouchers, unavailableCoupons]);

    const titleAvailableSaleRules = useMemo(
        () =>
            t('text_available_sales_rule', {
                count: availableSalesRules?.length,
                defaultValue: 'Bạn có {{count}} mã giảm giá có thể sử dụng',
            }),
        [availableSalesRules, t]
    );

    const titleUnavailableSalesRules = useMemo(
        () =>
            t('text_unavailable_sales_rule', {
                count: unavailableSalesRules?.length,
                defaultValue: 'Bạn có {{count}} mã giảm giá chưa đủ điều kiện',
            }),
        [t, unavailableSalesRules]
    );

    const salesRuleSections: CouponSectionType[] = useMemo(
        () => [
            {
                isDisabled: false,
                title: titleAvailableSaleRules,
                data: availableSalesRules as CouponSectionType['data'],
            },
            {
                isDisabled: true,
                title: titleUnavailableSalesRules,
                data: unavailableSalesRules as CouponSectionType['data'],
            },
        ],
        [availableSalesRules, titleAvailableSaleRules, titleUnavailableSalesRules, unavailableSalesRules]
    );

    /**
     * Combined refresh handler that refreshes all data sources:
     * - Applied coupons (Magento)
     * - Available/applied vouchers (Marketing Service)
     * - FastCartTotals (server prices with discount)
     * - Checkout discount version (triggers dependent components)
     */
    const handleRefresh = useCallback(() => {
        refreshCoupons();
        refreshVouchers();
        retryServerPrices();
        checkoutStoreAPI.actions.incrementDiscountVersion();
    }, [refreshCoupons, refreshVouchers, retryServerPrices]);

    return {
        salesRuleSections,
        /** Coupon selection handler (toggle select/deselect) */
        onSelectCoupon,
        /** Currently selected coupon/voucher */
        selectedCoupon,
        /** Raw available coupons data from GraphQL query */
        availableCoupon,
        /** Applied coupons on the cart */
        appliedCoupons,
        /** Applied vouchers on the cart */
        appliedVouchers,
        /** Loading state for available coupons + rules */
        loadingAvailableCoupons,
        /** Loading state for applied coupons */
        loadingAppliedCoupons,
        /** Loading state for server-side coupon validation */
        validatingCoupons,
        /** Loading state for vouchers */
        loadingVouchers,
        /** Combined refresh: coupons + vouchers + FastCartTotals + discount version */
        onRefresh: handleRefresh,
    };
};
